import { useState, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import emailjs from "@emailjs/browser";
import {
  EMAILJS_PUBLIC_KEY,
  EMAILJS_SERVICE_ID,
  EMAILJS_TEMPLATE_ID,
  SUBJECT_OPTIONS,
} from "../config/contact";

const NAME_MAX = 200;
const MESSAGE_MIN = 10;
const MESSAGE_MAX = 5000;

/** Minimum ms after page load before a send is allowed (stops instant bot posts). */
const MIN_MS_BEFORE_SEND = 2500;

const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function isValidEmail(value) {
  const t = value.trim();
  if (!t) return false;
  return emailPattern.test(t);
}

function getInputErrorClasses(hasError) {
  return hasError
    ? "border-red-500 focus:border-red-500 dark:border-red-500/80"
    : "border-slate-200 dark:border-slate-800 focus:border-primary dark:focus:border-primary";
}

export const Contact = () => {
  const formLoadedAtRef = useRef(Date.now());
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [subject, setSubject] = useState(SUBJECT_OPTIONS[0]);
  const [message, setMessage] = useState("");
  /** Honeypot — must stay empty; bots often fill hidden "website" fields. */
  const [honeypotWebsite, setHoneypotWebsite] = useState("");

  const [errors, setErrors] = useState({});
  const [formError, setFormError] = useState("");
  const [success, setSuccess] = useState(false);
  const [sending, setSending] = useState(false);

  const clearFieldError = useCallback((key) => {
    setErrors((e) => {
      if (!e[key]) return e;
      const next = { ...e };
      delete next[key];
      return next;
    });
  }, []);

  const validate = useCallback(() => {
    const next = {};
    const n = fullName.trim();
    if (n.length < 2) {
      next.fullName = "Please enter your full name (at least 2 characters).";
    } else if (n.length > NAME_MAX) {
      next.fullName = `Please use at most ${NAME_MAX} characters for your name.`;
    }
    if (!isValidEmail(email)) {
      next.email = "Please enter a valid email address.";
    }
    if (!SUBJECT_OPTIONS.includes(subject)) {
      next.subject = "Please select a valid subject from the list.";
    }
    const m = message.trim();
    if (m.length < MESSAGE_MIN) {
      next.message = `Please enter a message of at least ${MESSAGE_MIN} characters.`;
    } else if (m.length > MESSAGE_MAX) {
      next.message = `Message is too long. Please use at most ${MESSAGE_MAX} characters.`;
    }
    if (!EMAILJS_SERVICE_ID || !EMAILJS_TEMPLATE_ID || !EMAILJS_PUBLIC_KEY) {
      next.emailjs =
        "Email service is not configured. Add EmailJS keys in .env and rebuild the app.";
    }
    return next;
  }, [fullName, email, subject, message]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError("");
    setSuccess(false);
    const next = validate();
    if (Object.keys(next).length > 0) {
      setErrors(next);
      return;
    }
    setErrors({});

    if (honeypotWebsite.trim() !== "") {
      setSuccess(true);
      setFullName("");
      setEmail("");
      setSubject(SUBJECT_OPTIONS[0]);
      setMessage("");
      setHoneypotWebsite("");
      return;
    }

    if (Date.now() - formLoadedAtRef.current < MIN_MS_BEFORE_SEND) {
      setFormError(
        "Please wait a moment before sending. This helps protect the form from automated submissions."
      );
      return;
    }

    setSending(true);
    try {
      await emailjs.send(
        EMAILJS_SERVICE_ID,
        EMAILJS_TEMPLATE_ID,
        {
          from_name: fullName.trim(),
          from_email: email.trim(),
          subject,
          message: message.trim(),
          to_name: "Flacon Jeans",
        },
        {
          publicKey: EMAILJS_PUBLIC_KEY,
        }
      );
      setSuccess(true);
      setFullName("");
      setEmail("");
      setSubject(SUBJECT_OPTIONS[0]);
      setMessage("");
      setHoneypotWebsite("");
    } catch (error) {
      const status = error?.status ? ` (status: ${error.status})` : "";
      const reason =
        typeof error?.text === "string" && error.text.trim()
          ? error.text.trim()
          : "";
      console.error("EmailJS send failed:", error);
      setFormError(
        reason
          ? `Email service error${status}: ${reason}`
          : "We couldn’t send your message right now. Please try again or reach us on WhatsApp."
      );
    } finally {
      setSending(false);
    }
  };

  const styles = `
        :root {
            --primary-color: #f97316;
        }
        .industrial-overlay {
            background: linear-gradient(rgba(15, 23, 42, 0.85), rgba(15, 23, 42, 0.85)), url('https://images.unsplash.com/photo-1555529771-835f59fc5efe?auto=format&fit=crop&q=80&w=2000');
            background-size: cover;
            background-position: center;
        }
        .material-symbols-outlined {
            font-variation-settings: 'FILL' 0, 'wght' 400, 'GRAD' 0, 'opsz' 24;
        }
`;

  const inputBase =
    "w-full bg-off-white dark:bg-slate-900 border-0 border-b-2 focus:ring-0 text-navy-dark dark:text-white p-4 font-medium transition-colors";
  const labelCls =
    "text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500 ml-1";
  const errCls = "text-sm text-red-600 dark:text-red-400 mt-1.5";

  return (
    <>
      <style type="text/tailwindcss">{styles}</style>

      <header className="relative h-[350px] flex items-center overflow-hidden">
        <div className="absolute inset-0 industrial-overlay" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
          <div className="max-w-2xl">
            <span className="inline-block bg-primary text-white text-[10px] font-bold px-3 py-1 mb-4 uppercase tracking-[0.3em]">
              Contact Us
            </span>
            <h1 className="text-4xl md:text-6xl font-display font-bold text-white mb-4 uppercase leading-tight">
              Get in{" "}
              <span className="text-primary span-projection">Touch</span> <br />
              With Our Team
            </h1>
            <p className="text-slate-300 text-lg font-light max-w-lg">
              Whether you&apos;re looking for wholesale opportunities or have a
              product enquiry, we&apos;re here to help.
            </p>
          </div>
        </div>
      </header>
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-16">
            <div className="space-y-12 text-left">
              <section>
                <h2 className="text-2xl font-display font-bold uppercase tracking-tight text-navy-dark dark:text-white mb-6">
                  Direct{" "}
                  <span className="text-primary span-projection-on-light">
                    Contact
                  </span>
                </h2>
                <div className="space-y-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-primary/10 rounded flex items-center justify-center shrink-0">
                      <span className="material-symbols-outlined text-primary">
                        chat_bubble
                      </span>
                    </div>
                    <div>
                      <p className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-1">
                        WhatsApp Support
                      </p>
                      <div className="flex flex-col gap-2">
                        <a
                          className="text-lg font-bold hover:text-primary transition-colors flex items-center gap-2"
                          href="https://wa.me/919870316550"
                        >
                          9870316550{" "}
                          <span className="text-[10px] bg-green-100 text-green-700 px-2 py-0.5 rounded uppercase">
                            Active
                          </span>
                        </a>
                        <a
                          className="text-lg font-bold hover:text-primary transition-colors flex items-center gap-2"
                          href="https://wa.me/919320541732"
                        >
                          9320541732{" "}
                          <span className="text-[10px] bg-green-100 text-green-700 px-2 py-0.5 rounded uppercase">
                            Active
                          </span>
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
              </section>
              <section>
                <h2 className="text-2xl font-display font-bold uppercase tracking-tight text-navy-dark dark:text-white mb-6">
                  office & Manufacturing{" "}
                  <span className="text-primary span-projection-on-light">
                    Hub
                  </span>
                </h2>
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-primary/10 rounded flex items-center justify-center shrink-0">
                    <span className="material-symbols-outlined text-primary">
                      factory
                    </span>
                  </div>
                  <div>
                    <p className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-2">
                      Factory & office Address
                    </p>
                    <p className="text-sm font-semibold leading-relaxed text-slate-700 dark:text-slate-300">
                      E/2A, Gala No - 10, <br />
                      Asmeeta Textile Park, Kongaon, <br />
                      Kalyan Bhiwandi Road, <br />
                      Kalyan - 421302, <br />
                      Maharashtra, India
                    </p>
                  </div>
                </div>
              </section>
              <section className="relative h-64 w-full bg-slate-200 rounded overflow-hidden group">
                <img
                  alt="Factory location map for Flacon Jeans in Mumbai"
                  className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-500"
                  src="https://lh3.googleusercontent.com/aida-public/AB6AXuDbtWrFFaGmO5uXNGt_8TMt7ecHh1CMf7VQfCR8Nw_iH60yuO4fikn15KDY4H8LYrU3R1eVDoWGhLnl7X5SM6OYs4DFBsGd52xfqq3OtRU_lM6YF4HM4dsvu3Kj7-WRWjP3gEBVEu6BVmRM-ddt6F5URXAW1-Cpwk5vC8lCiiDu7LfW3gZdM2Ac4Bkc-THKUBumHd5UB2qqw5qUNwl1n799NdcVuZF-224LjyIQhY0nlpVtTtXfEJoLwlDfYm0pxKLPdOGq-EHUS_lw"
                />
                <div class="absolute inset-0 bg-navy-dark/40 flex items-center justify-center">
                  <Link to="https://www.google.com/maps/place/asmeeta+textile+park,+Kongaon,+Maharashtra+421311/@19.2546847,73.1008904,17z/data=!4m6!3m5!1s0x3be7960fb5e9495d:0x6e18730fee6f1aa4!8m2!3d19.2544454!4d73.1021809!16s%2Fg%2F11lmn_f1bf!5m1!1e1?entry=ttu&g_ep=EgoyMDI2MDQxOS4wIKXMDSoASAFQAw%3D%3D" class="bg-white text-navy-dark px-6 py-2 text-xs font-bold uppercase tracking-widest shadow-xl hover:bg-primary hover:text-white transition-colors"
                    target="_blank" rel="noopener noreferrer">
                    Open in Maps
                  </Link>
                </div>
              </section>
            </div>
            <div className="lg:col-span-2">
              <div className="bg-white dark:bg-slate-900/50 p-8 md:p-12 border border-slate-100 dark:border-slate-800 shadow-sm">
                <h2 className="text-3xl font-display font-bold uppercase tracking-tight text-navy-dark dark:text-white mb-2">
                  Product{" "}
                  <span className="text-primary span-projection-on-light">
                    Enquiry
                  </span>
                </h2>
                <p className="text-slate-500 dark:text-slate-400 text-sm mb-6">
                  Fill out the form below and our sales representative will get
                  back to you within 24 hours.
                </p>

                {success && (
                  <div
                    className="mb-6 rounded-md border border-emerald-200 bg-emerald-50 p-4 text-emerald-900"
                    role="status"
                    aria-live="polite"
                  >
                    Mail sent successfully.
                  </div>
                )}

                {formError && (
                  <div
                    className="mb-6 rounded-md border border-red-200 bg-red-50 p-4 text-red-900"
                    role="alert"
                    aria-live="assertive"
                  >
                    {formError}
                  </div>
                )}
                {errors.emailjs && (
                  <div
                    className="mb-6 rounded-md border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900"
                    role="status"
                    aria-live="polite"
                  >
                    {errors.emailjs}
                  </div>
                )}

                <form
                  className="relative grid grid-cols-1 md:grid-cols-2 gap-8 text-left"
                  onSubmit={handleSubmit}
                  noValidate
                >
                  <div
                    className="absolute left-[-9999px] top-auto h-0 w-0 overflow-hidden"
                    aria-hidden="true"
                  >
                    <label htmlFor="contact-website-hp">Website</label>
                    <input
                      id="contact-website-hp"
                      name="website"
                      type="text"
                      tabIndex={-1}
                      autoComplete="off"
                      value={honeypotWebsite}
                      onChange={(e) => setHoneypotWebsite(e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <label
                      className={labelCls}
                      htmlFor="contact-full-name"
                    >
                      Full name
                    </label>
                    <input
                      id="contact-full-name"
                      name="fullName"
                      type="text"
                      autoComplete="name"
                      aria-invalid={Boolean(errors.fullName)}
                      aria-describedby={
                        errors.fullName ? "contact-full-name-error" : undefined
                      }
                      className={`${inputBase} ${getInputErrorClasses(
                        Boolean(errors.fullName)
                      )}`}
                      placeholder="Your full name"
                      value={fullName}
                      onChange={(e) => {
                        setFullName(e.target.value);
                        clearFieldError("fullName");
                        setSuccess(false);
                      }}
                    />
                    {errors.fullName && (
                      <p id="contact-full-name-error" className={errCls}>
                        {errors.fullName}
                      </p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <label
                      className={labelCls}
                      htmlFor="contact-email"
                    >
                      Email address
                    </label>
                    <input
                      id="contact-email"
                      name="email"
                      type="email"
                      inputMode="email"
                      autoComplete="email"
                      aria-invalid={Boolean(errors.email)}
                      aria-describedby={
                        errors.email ? "contact-email-error" : undefined
                      }
                      className={`${inputBase} ${getInputErrorClasses(
                        Boolean(errors.email)
                      )}`}
                      placeholder="email@example.com"
                      value={email}
                      onChange={(e) => {
                        setEmail(e.target.value);
                        clearFieldError("email");
                        setSuccess(false);
                      }}
                    />
                    {errors.email && (
                      <p id="contact-email-error" className={errCls}>
                        {errors.email}
                      </p>
                    )}
                  </div>
                  <div className="md:col-span-2 space-y-2">
                    <label
                      className={labelCls}
                      htmlFor="contact-subject"
                    >
                      Subject
                    </label>
                    <select
                      id="contact-subject"
                      name="subject"
                      aria-invalid={Boolean(errors.subject)}
                      aria-describedby={
                        errors.subject ? "contact-subject-error" : undefined
                      }
                      className={`${inputBase} ${getInputErrorClasses(
                        Boolean(errors.subject)
                      )}`}
                      value={subject}
                      onChange={(e) => {
                        setSubject(e.target.value);
                        clearFieldError("subject");
                        setSuccess(false);
                      }}
                    >
                      {SUBJECT_OPTIONS.map((opt) => (
                        <option key={opt} value={opt}>
                          {opt}
                        </option>
                      ))}
                    </select>
                    {errors.subject && (
                      <p id="contact-subject-error" className={errCls}>
                        {errors.subject}
                      </p>
                    )}
                  </div>
                  <div className="md:col-span-2 space-y-2">
                    <label
                      className={labelCls}
                      htmlFor="contact-message"
                    >
                      Message
                    </label>
                    <textarea
                      id="contact-message"
                      name="message"
                      rows={5}
                      aria-invalid={Boolean(errors.message)}
                      aria-describedby={
                        errors.message ? "contact-message-error" : undefined
                      }
                      className={`${inputBase} ${getInputErrorClasses(
                        Boolean(errors.message)
                      )} resize-none`}
                      placeholder="How can we assist you with our premium denim collection?"
                      value={message}
                      onChange={(e) => {
                        setMessage(e.target.value);
                        clearFieldError("message");
                        setSuccess(false);
                      }}
                    />
                    {errors.message && (
                      <p id="contact-message-error" className={errCls}>
                        {errors.message}
                      </p>
                    )}
                  </div>

                  <div className="md:col-span-2 mt-2">
                    <button
                      className="w-full md:w-auto bg-navy-dark dark:bg-white text-white dark:text-navy-dark px-12 py-4 font-bold uppercase tracking-[0.2em] hover:bg-primary hover:text-white dark:hover:bg-primary dark:hover:text-white transition-all shadow-lg disabled:cursor-not-allowed disabled:opacity-50"
                      type="submit"
                      disabled={sending}
                    >
                      {sending ? "Sending…" : "Send Enquiry"}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};
